import React, { useEffect, useRef, memo } from 'react';
import { Box } from '@mui/material';
import styles from './DocumentContent.module.scss';
import { DocumentContentProps } from './types';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import ConfirmModal from '../../../../common/confirm-modal/ConfirmModal';
import { useSearchParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { selectIsOverlayShown } from '../../../../slices/show-progress-overlay/showProgressOverlay';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';

const DocumentContent = ({
  documentHTML,
  saveDocument,
  setDocumentHTML,
  contentEditable,
  setContentEditable,
  doc,
  templateCreationEnabled,
  showConfirmationModal,
  setShowConfirmationModal,
  isImage,
  fileLoading,
  fileBlobURL,
}: DocumentContentProps) => {
  //const navigate = useNavigate();
  const contentBlockRef = useRef<HTMLDivElement | null>(null);
  const [params] = useSearchParams();
  const editDocumentParam = params.get('editDocument');
  const contractGenerated = params.get('contractGenerated');
  const isOverlayShown = useAppSelector(selectIsOverlayShown);
  const editorMenu = contractGenerated ? 'edit insert view format table tools' : false;
  const editorTools = false; /*contractGenerated
    ? 'undo redo| blocks fontfamily fontsize | bold italic underline strikethrough | link table | spellcheckdialog a11ycheck typography uploadcare | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat'
    : false*/
  const editorPlugins =
    import.meta.env.VITE_ENV_TYPE === 'PROD'
      ? [
          'anchor',
          'autolink',
          'charmap',
          'codesample',
          'emoticons',
          'link',
          'lists',
          'searchreplace',
          'table',
          'visualblocks',
          'wordcount',
        ]
      : [
          'anchor',
          'autolink',
          'charmap',
          'codesample',
          'emoticons',
          'link',
          'lists',
          'searchreplace',
          'table',
          'visualblocks',
          'wordcount',
          'code',
        ];
  useEffect(() => {
    let clickOutsideTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentEditable &&
        contentBlockRef.current &&
        !contentBlockRef.current.contains(event.target as Node) /*&&*/
        //(!popperNode || !popperNode.contains(event.target as Node)) // Ahora ignora correctamente los clics en el menú
      ) {
        /* if (
                    targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
                    targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
                ) {
                  return;
                }*/
        clickOutsideTimeout = setTimeout(() => {
          //setShowConfirmationModal(true);
        }, 0);
      }
    };

    //if (contentEditable) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (clickOutsideTimeout) clearTimeout(clickOutsideTimeout);
    };
  }, [contentEditable]);

  return (
    <>
      <Box className={styles.container} ref={contentBlockRef}>
        <Box className={styles.mainContent}>
          <div className={`${styles.main_container} ${templateCreationEnabled ? styles.w_50 : ''}`}>
            {doc && 'du_filetype' in doc && doc.du_filetype?.[0] === 'pdf' ? (
              <iframe
                src={fileBlobURL}
                title={doc?.name || 'Document preview'}
                className={styles.frame}
              />
            ) : (
              <div id='editor' className={'main-editor-container'}>
                {isImage ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <img
                      src={fileBlobURL}
                      alt=''
                      style={{ objectFit: 'contain', height: 'auto', maxHeight: '90%' }}
                    />
                  </div>
                ) : (
                  <Editor
                    tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                    licenseKey='gpl'
                    disabled={
                      (!contentEditable && editDocumentParam !== 'true') || templateCreationEnabled
                    }
                    init={{
                      content_style:
                        '#tinymce {max-width: 210mm; margin-left: auto; margin-right: auto; img {display: none !important;} tox-promotion {display: none !important;}',
                      menubar: editorMenu,
                      plugins: editorPlugins,
                      toolbar: editorTools,
                      ai_request: (request: any, respondWith: any) =>
                        respondWith.string(() =>
                          Promise.reject('See docs to implement AI Assistant')
                        ),
                      uploadcare_public_key: 'b97265d9de273c24383a',
                      // XSS prevention: strip <script> tags and on* event attributes from pasted/loaded content
                      invalid_elements: 'script,object,embed,applet,iframe,form,input,button',
                      extended_valid_elements: '',
                      cleanup_on_startup: true,
                      convert_unsafe_embeds: true,
                      sandbox_iframes: true,
                      // Restrict link protocols to safe schemes only
                      link_assume_external_targets: false,
                      allow_script_urls: false,
                    }}
                    value={documentHTML}
                    onEditorChange={(value) => {
                      if (value) {
                        setDocumentHTML(value);
                      } else {
                        setDocumentHTML('');
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </Box>
        {showConfirmationModal && (
          <ConfirmModal
            open={showConfirmationModal}
            onClose={() => {
              saveDocument?.();
              setShowConfirmationModal(false);
            }}
            onConfirm={() => {
              setContentEditable(false);
              setShowConfirmationModal(false);
            }}
          />
        )}
      </Box>
      {fileLoading && !isOverlayShown && <ProgressOverlay position='absolute' />}
    </>
  );
};

export default memo(DocumentContent);
